import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "@/utils/appToast";
import { api } from "@/utils/api";
import TextArea from "@/shared/textArea/textArea";
import Inputfield from "@/shared/inputfield";
import CustomButton from "@/shared/button/customButton";
import AssignedVoucherPicker from "@/components/vouchers/AssignedVoucherPicker";
import { filterUsableVouchersForAudience } from "@/utils/vouchers";
import { getBackendCallErrorMessage } from "@/utils/backendErrorMessage";
import type { MyVoucher, ProviderVoucherPreview } from "@/types/jobPaymentTypes";

type NewProposalFormProps = {
  requestQuoteJobId: string;
  onSuccess?: () => void;
  /** Close the parent popup (defaults to onSuccess). */
  onClose?: () => void;
  /** When omitted, inferred from whether an existing proposal was loaded. */
  proposalMode?: "create" | "edit";
};

type PaymentModel = "hourly" | "fixed";

type LoadGate =
  | { kind: "loading" }
  | { kind: "ready" }
  | { kind: "converted"; message: string }
  | { kind: "error"; message: string };

const getProposalVoucherCode = (proposal: any): string | null => {
  const code =
    proposal?.voucherCode ||
    proposal?.providerVoucherCode ||
    proposal?.voucher?.code ||
    null;
  if (!code || typeof code !== "string") return null;
  return code.trim().toUpperCase() || null;
};

const isConvertedQuoteMessage = (message?: string | null): boolean => {
  const m = String(message || "").toLowerCase();
  if (!m) return false;
  return (
    m.includes("converted") ||
    m.includes("already a job") ||
    m.includes("already been converted") ||
    m.includes("no longer a request") ||
    m.includes("is no longer open") ||
    (m.includes("accepted") && m.includes("job"))
  );
};

type ProposalFieldErrors = {
  description?: string;
  budget?: string;
  hourlyRate?: string;
  totalHours?: string;
  voucherCode?: string;
};

const NewProposalForm = ({
  requestQuoteJobId,
  onSuccess,
  onClose,
  proposalMode,
}: NewProposalFormProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useSelector((state: any) => state.user || {});

  const [loadGate, setLoadGate] = useState<LoadGate>({ kind: "loading" });
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [paymentModel, setPaymentModel] = useState<PaymentModel>("fixed");
  const [hourlyRate, setHourlyRate] = useState<string>("");
  const [totalHours, setTotalHours] = useState<string>("");
  const [budget, setBudget] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [proposalStatus, setProposalStatus] = useState<string | null>(null);
  const [resolvedRequestQuoteJobId, setResolvedRequestQuoteJobId] =
    useState<string>("");

  const [voucherCode, setVoucherCode] = useState("");
  const [appliedVoucherCode, setAppliedVoucherCode] = useState<string | null>(
    null
  );
  const [voucherPreview, setVoucherPreview] =
    useState<ProviderVoucherPreview | null>(null);
  const [isApplyingVoucher, setIsApplyingVoucher] = useState(false);
  const [usableAssignedVouchers, setUsableAssignedVouchers] = useState<
    MyVoucher[]
  >([]);
  const [fieldErrors, setFieldErrors] = useState<ProposalFieldErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  const clearFieldError = (field: keyof ProposalFieldErrors) => {
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const scrollToFirstInvalid = () => {
    const firstInvalid = document.querySelector(
      "[data-proposal-invalid='true']"
    );
    firstInvalid?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const handleClose = () => {
    (onClose || onSuccess)?.();
  };

  const isReadOnly =
    proposalStatus === "accepted" || proposalStatus === "rejected";

  const resolvedProposalMode: "create" | "edit" =
    proposalMode ??
    (proposalStatus === null ||
    String(proposalStatus).toLowerCase() === "awaiting"
      ? "create"
      : "edit");

  const proposalHeading =
    resolvedProposalMode === "create"
      ? t("create_proposal") || "Create proposal"
      : t("edit_proposal") || "Edit proposal";

  const proposalSubmitLabel =
    resolvedProposalMode === "create"
      ? t("create_proposal") || "Create proposal"
      : t("submit_proposal") || "Submit proposal";

  const computedBudget = useMemo(() => {
    if (paymentModel !== "hourly") return Number(budget || 0);
    const r = Number(hourlyRate || 0);
    const h = Number(totalHours || 0);
    if (!Number.isFinite(r) || !Number.isFinite(h)) return 0;
    return r * h;
  }, [paymentModel, hourlyRate, totalHours, budget]);

  const clearAppliedVoucher = () => {
    setAppliedVoucherCode(null);
    setVoucherPreview(null);
  };

  const handleApplyProviderVoucher = async (codeOverride?: string) => {
    const code = (
      typeof codeOverride === "string" ? codeOverride : voucherCode
    )
      .trim()
      .toUpperCase();
    if (!code) {
      toast.error(t("voucher_invalid"));
      return;
    }
    if (!computedBudget || computedBudget <= 0) {
      toast.error(t("provider_voucher_need_budget"));
      return;
    }

    setIsApplyingVoucher(true);
    try {
      const res = await api.previewProviderVoucher({
        voucherCode: code,
        budget: computedBudget,
        jobId: resolvedRequestQuoteJobId || undefined,
      });
      if (res?.success && res.data?.voucherCode) {
        setVoucherPreview(res.data);
        setAppliedVoucherCode(res.data.voucherCode);
        setVoucherCode(res.data.voucherCode);
        toast.success(
          t("platform_fee_discount_applied", {
            amount: Number(res.data.providerDiscountApplied || 0).toFixed(2),
          })
        );
      } else {
        clearAppliedVoucher();
        toast.error(res?.message || t("voucher_invalid"));
      }
    } catch {
      clearAppliedVoucher();
      toast.error(t("voucher_invalid"));
    } finally {
      setIsApplyingVoucher(false);
    }
  };

  const handleClearProviderVoucher = () => {
    setVoucherCode("");
    clearAppliedVoucher();
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.getMyVouchers();
        if (cancelled || !res?.success) return;
        setUsableAssignedVouchers(
          filterUsableVouchersForAudience(res.data || [], "provider")
        );
      } catch {
        if (!cancelled) setUsableAssignedVouchers([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const resolveRequestQuoteJobId = async (idOrUid: string) => {
      if (/^[a-f\d]{24}$/i.test(idOrUid)) return { mongoId: idOrUid as string };

      const jobRes = await api.getProposalDetails({
        id: idOrUid,
        userId: user?._id,
      });

      if (!jobRes || !("data" in jobRes) || !jobRes.success) {
        const message =
          getBackendCallErrorMessage(jobRes) ||
          (jobRes as any)?.message ||
          "Request quote job not found";
        return { mongoId: null as string | null, errorMessage: message };
      }
      return { mongoId: (jobRes.data?._id as string) || null };
    };

    const hydrateVoucher = async (
      code: string,
      proposalBudget: number,
      jobId: string
    ) => {
      if (!code || !(proposalBudget > 0)) {
        setVoucherCode(code || "");
        return;
      }
      setVoucherCode(code);
      try {
        const res = await api.previewProviderVoucher({
          voucherCode: code,
          budget: proposalBudget,
          jobId: jobId || undefined,
        });
        if (cancelled) return;
        if (res?.success && res.data?.voucherCode) {
          setVoucherPreview(res.data);
          setAppliedVoucherCode(res.data.voucherCode);
          setVoucherCode(res.data.voucherCode);
        } else {
          setAppliedVoucherCode(code);
          setVoucherPreview(null);
        }
      } catch {
        if (!cancelled) {
          setAppliedVoucherCode(code);
          setVoucherPreview(null);
        }
      }
    };

    const loadExistingProposal = async () => {
      if (!requestQuoteJobId) return;
      try {
        setIsLoading(true);
        setLoadGate({ kind: "loading" });

        const resolved = await resolveRequestQuoteJobId(requestQuoteJobId);
        const mongoJobId = resolved.mongoId;
        if (!mongoJobId) {
          if (!cancelled) {
            const message =
              resolved.errorMessage ||
              t("requestQuoteJobNotFound") ||
              "Unable to load this quote request.";
            setLoadGate(
              isConvertedQuoteMessage(message)
                ? {
                    kind: "converted",
                    message:
                      t("quoteConvertedToJob") ||
                      "This quote request was accepted and converted into a job. You can no longer edit a proposal here.",
                  }
                : { kind: "error", message }
            );
          }
          return;
        }
        if (cancelled) return;
        setResolvedRequestQuoteJobId(mongoJobId);

        const res = await api.getProposalsForRequestQuote(mongoJobId);
        if (res?.success === false) {
          const message =
            getBackendCallErrorMessage(res) ||
            res?.message ||
            "Failed to load proposal";
          if (!cancelled) {
            setLoadGate(
              isConvertedQuoteMessage(message)
                ? {
                    kind: "converted",
                    message:
                      t("quoteConvertedToJob") ||
                      "This quote request was accepted and converted into a job. You can no longer edit a proposal here.",
                  }
                : { kind: "error", message }
            );
          }
          return;
        }

        const body = res?.data?.proposals
          ? res.data
          : res?.data?.data || res?.data || res;
        const existing = body?.proposals?.[0];
        if (cancelled) return;

        if (!existing) {
          setProposalStatus(null);
          setLoadGate({ kind: "ready" });
          return;
        }

        const model: PaymentModel =
          existing?.paymentType === "hourly" ? "hourly" : "fixed";

        setPaymentModel(model);
        setHourlyRate(
          existing?.hourlyRate != null ? String(existing.hourlyRate) : ""
        );
        setTotalHours(
          existing?.totalHours != null ? String(existing.totalHours) : ""
        );
        setBudget(existing?.budget != null ? String(existing.budget) : "");
        setDescription(existing?.description || "");
        setProposalStatus(existing?.status || null);
        setLoadGate({ kind: "ready" });

        const existingVoucher = getProposalVoucherCode(existing);
        const existingBudget = Number(existing?.budget || 0);
        if (existingVoucher) {
          await hydrateVoucher(existingVoucher, existingBudget, mongoJobId);
        } else {
          setVoucherCode("");
          clearAppliedVoucher();
        }
      } catch (e: any) {
        console.error("Failed to load existing proposal:", e);
        const message = e?.message || "Failed to load proposal";
        if (!cancelled) {
          setLoadGate(
            isConvertedQuoteMessage(message)
              ? {
                  kind: "converted",
                  message:
                    t("quoteConvertedToJob") ||
                    "This quote request was accepted and converted into a job. You can no longer edit a proposal here.",
                }
              : { kind: "error", message }
          );
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    void loadExistingProposal();
    return () => {
      cancelled = true;
    };
  }, [requestQuoteJobId, user?._id, t]);

  const validate = () => {
    const nextErrors: ProposalFieldErrors = {};

    if (!description.trim()) {
      nextErrors.description =
        t("description_required") || "Please add a description.";
    }

    if (paymentModel === "hourly") {
      if (!hourlyRate) {
        nextErrors.hourlyRate =
          t("hourly_rate_required") || "Hourly rate is required.";
      }
      if (!totalHours) {
        nextErrors.totalHours =
          t("total_hours_required") || "Total hours are required.";
      }
      const r = Number(hourlyRate);
      const h = Number(totalHours);
      if (
        hourlyRate &&
        totalHours &&
        (!Number.isFinite(r) || !Number.isFinite(h) || r <= 0 || h <= 0)
      ) {
        nextErrors.hourlyRate =
          t("invalid_hourly_values") || "Please enter valid hourly values.";
        nextErrors.totalHours =
          t("invalid_hourly_values") || "Please enter valid hourly values.";
      }
    } else if (!budget) {
      nextErrors.budget = t("budget_required") || "Amount is required.";
    } else {
      const b = Number(budget);
      if (!Number.isFinite(b) || b <= 0) {
        nextErrors.budget =
          t("invalid_budget") || "Please enter a valid amount.";
      }
    }

    const codeFromInput = voucherCode.trim().toUpperCase();
    if (codeFromInput && codeFromInput !== appliedVoucherCode) {
      nextErrors.voucherCode = t("voucher_apply_required");
    }

    if (
      appliedVoucherCode &&
      voucherPreview &&
      computedBudget > 0 &&
      Math.abs(Number(voucherPreview.budget) - computedBudget) > 0.009
    ) {
      nextErrors.voucherCode = t("provider_voucher_reapply_budget");
      clearAppliedVoucher();
    }

    if (Object.keys(nextErrors).length > 0) {
      setFieldErrors(nextErrors);
      scrollToFirstInvalid();
      return false;
    }

    setFieldErrors({});
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    if (isReadOnly) {
      setSubmitError(
        proposalStatus === "accepted"
          ? "This proposal has already been accepted"
          : "This proposal can no longer be edited"
      );
      return;
    }
    if (!validate()) return;
    if (!resolvedRequestQuoteJobId) {
      setSubmitError("Request quote job is not ready yet.");
      return;
    }

    try {
      setIsSubmitting(true);

      const payload: Record<string, any> = {
        paymentType: paymentModel,
        description: description.trim(),
        budget: computedBudget,
        ...(appliedVoucherCode ? { voucherCode: appliedVoucherCode } : {}),
      };

      if (paymentModel === "hourly") {
        payload.hourlyRate = Number(hourlyRate);
        payload.totalHours = Number(totalHours);
      }

      const res = await api.createOrUpdateRequestQuoteProposal(
        resolvedRequestQuoteJobId,
        payload
      );

      if (!res?.success && res?.success !== undefined) {
        throw new Error(
          getBackendCallErrorMessage(res) ||
            res?.message ||
            "Failed to save proposal"
        );
      }

      toast.success(t("proposal_sent") || "Proposal saved successfully.");
      onSuccess?.();
    } catch (err: any) {
      toast.error(
        err?.message || t("something_went_wrong") || "Something went wrong."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 bg-white">
      {loadGate.kind === "loading" && (
        <div className="py-10 text-center text-sm text-[#424654]">
          {t("loading") || "Loading…"}
        </div>
      )}

      {(loadGate.kind === "converted" || loadGate.kind === "error") && (
        <div className="flex flex-col gap-4">
          <h2 className="text-xl font-semibold text-[#111c2d]">
            {loadGate.kind === "converted"
              ? t("quoteConvertedTitle") || "Quote converted to a job"
              : t("edit_proposal") || "Request Quote Proposal"}
          </h2>
          <div
            className={`rounded-xl border px-4 py-3 text-sm ${
              loadGate.kind === "converted"
                ? "border-amber-200 bg-amber-50 text-amber-900"
                : "border-red-200 bg-red-50 text-red-800"
            }`}
          >
            {loadGate.message}
          </div>
          <div className="flex flex-col sm:flex-row gap-3 justify-end pt-1">
            <button
              type="button"
              onClick={handleClose}
              className="h-10 px-5 rounded-lg border border-[#d8e3fb] bg-white text-sm font-semibold text-[#4285F4] hover:bg-[#e7eeff] transition-colors"
            >
              {t("close") || "Close"}
            </button>
            {loadGate.kind === "converted" && (
              <button
                type="button"
                onClick={() => {
                  handleClose();
                  navigate("/my-jobs");
                }}
                className="h-10 px-5 rounded-lg bg-[#4285F4] text-white text-sm font-semibold shadow-sm hover:bg-[#4285F4] transition-colors"
              >
                {t("viewMyJobs") || "View my jobs"}
              </button>
            )}
          </div>
        </div>
      )}

      {loadGate.kind === "ready" && (
        <>
      <h2 className="text-xl font-semibold text-[#111c2d] mb-1">
        {proposalHeading}
      </h2>
      <p className="text-sm text-[#424654] mb-4">
        {resolvedProposalMode === "create"
          ? t("request_quote_proposal_create_note") ||
            "Submit your proposal for this quote request."
          : t("request_quote_proposal_note") ||
            "Submit or update your proposal for this quote request."}
      </p>

      {isReadOnly && (
        <div
          className={`mb-4 rounded-xl border px-4 py-3 text-sm ${
            proposalStatus === "accepted"
              ? "border-green-200 bg-green-50 text-green-800"
              : "border-red-200 bg-red-50 text-red-800"
          }`}
        >
          {proposalStatus === "accepted"
            ? t("proposalAcceptedReadOnly") ||
              "This proposal was accepted. Editing is disabled."
            : t("proposalRejectedReadOnly") ||
              "This proposal was rejected. Editing is disabled."}
        </div>
      )}

      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        {submitError && (
          <div
            className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
            role="alert"
          >
            {submitError}
          </div>
        )}

        <div className="flex gap-3">
          <button
            type="button"
            disabled={isReadOnly}
            onClick={() => {
              setPaymentModel("fixed");
              clearAppliedVoucher();
              setFieldErrors({});
            }}
            className={`h-9 px-4 rounded-lg text-sm font-semibold border disabled:opacity-60 transition-colors ${
              paymentModel === "fixed"
                ? "bg-[#4285F4] text-white border-[#4285F4]"
                : "bg-white text-[#4285F4] border-[#d8e3fb] hover:bg-[#e7eeff]"
            }`}
          >
            {t("fixed") || "Fixed"}
          </button>
          <button
            type="button"
            disabled={isReadOnly}
            onClick={() => {
              setPaymentModel("hourly");
              clearAppliedVoucher();
              setFieldErrors({});
            }}
            className={`h-9 px-4 rounded-lg text-sm font-semibold border disabled:opacity-60 transition-colors ${
              paymentModel === "hourly"
                ? "bg-[#4285F4] text-white border-[#4285F4]"
                : "bg-white text-[#4285F4] border-[#d8e3fb] hover:bg-[#e7eeff]"
            }`}
          >
            {t("hourly") || "Hourly"}
          </button>
        </div>

        {paymentModel === "fixed" ? (
          <div data-proposal-invalid={fieldErrors.budget ? "true" : undefined}>
            <Inputfield
              name="budget"
              label={t("budget") || "Amount"}
              type="number"
              required
              disabled={isReadOnly}
              value={budget}
              placeholder={t("enter_total_budget")}
              inputClassName="placeholder:text-placeholder"
              error={fieldErrors.budget}
              touched={!!fieldErrors.budget}
              handldChange={(e: any) => {
                setBudget(e.target.value);
                if (appliedVoucherCode) clearAppliedVoucher();
                clearFieldError("budget");
              }}
            />
            {fieldErrors.budget && (
              <p className="mt-1 text-xs text-red-500" role="alert">
                {fieldErrors.budget}
              </p>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div
              data-proposal-invalid={fieldErrors.hourlyRate ? "true" : undefined}
            >
              <Inputfield
                name="hourlyRate"
                label={t("hourly_rate") || "Hourly rate"}
                type="number"
                required
                disabled={isReadOnly}
                value={hourlyRate}
                placeholder={t("enter_hourly_rate")}
                inputClassName="placeholder:text-placeholder"
                error={fieldErrors.hourlyRate}
                touched={!!fieldErrors.hourlyRate}
                handldChange={(e: any) => {
                  setHourlyRate(e.target.value);
                  if (appliedVoucherCode) clearAppliedVoucher();
                  clearFieldError("hourlyRate");
                }}
              />
              {fieldErrors.hourlyRate && (
                <p className="mt-1 text-xs text-red-500" role="alert">
                  {fieldErrors.hourlyRate}
                </p>
              )}
            </div>
            <div
              data-proposal-invalid={fieldErrors.totalHours ? "true" : undefined}
            >
              <Inputfield
                name="totalHours"
                label={t("total_hours") || "Total hours"}
                type="number"
                required
                disabled={isReadOnly}
                value={totalHours}
                placeholder={t("enter_total_hours")}
                inputClassName="placeholder:text-placeholder"
                error={fieldErrors.totalHours}
                touched={!!fieldErrors.totalHours}
                handldChange={(e: any) => {
                  setTotalHours(e.target.value);
                  if (appliedVoucherCode) clearAppliedVoucher();
                  clearFieldError("totalHours");
                }}
              />
              {fieldErrors.totalHours && (
                <p className="mt-1 text-xs text-red-500" role="alert">
                  {fieldErrors.totalHours}
                </p>
              )}
            </div>
          </div>
        )}

        <div data-proposal-invalid={fieldErrors.description ? "true" : undefined}>
          <TextArea
            label={t("description") || "Description"}
            labelClassName="!text-[#111c2d] text-base"
            name="description"
            required
            disabled={isReadOnly}
            placeholder={t("enter_description")}
            className="placeholder:text-placeholder"
            value={description}
            error={fieldErrors.description}
            touched={!!fieldErrors.description}
            handldChange={(e: any) => {
              setDescription(e.target.value);
              clearFieldError("description");
            }}
          />
        </div>

        <div
          className={`rounded-xl border bg-[#f9f9ff] p-4 ${
            fieldErrors.voucherCode ? "border-red-300" : "border-[#d8e3fb]"
          }`}
          data-proposal-invalid={fieldErrors.voucherCode ? "true" : undefined}
        >
          <label className="block mb-1 text-sm font-semibold text-[#111c2d]">
            {t("provider_voucher_section_title")}
          </label>
          <p className="text-xs text-[#424654] mb-3 leading-snug">
            {t("provider_voucher_help_apply")}
          </p>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <input
              type="text"
              name="voucherCode"
              value={voucherCode}
              disabled={isReadOnly || isApplyingVoucher}
              onChange={(e) => {
                const next = e.target.value.toUpperCase();
                setVoucherCode(next);
                clearFieldError("voucherCode");
                if (appliedVoucherCode && next.trim() !== appliedVoucherCode) {
                  clearAppliedVoucher();
                }
              }}
              placeholder={t("provider_voucher_placeholder")}
              className="flex-1 rounded-lg border border-[#d8e3fb] bg-white px-3 py-2 text-[#111c2d] placeholder:text-placeholder disabled:opacity-60"
              autoComplete="off"
              spellCheck={false}
            />
            <div className="flex items-center gap-3">
              <CustomButton
                type="button"
                label={t("apply_voucher")}
                className="!rounded-lg !bg-[#4285F4] px-5 py-2 hover:!bg-[#4285F4]"
                labelStyle="text-sm text-white"
                isLoading={isApplyingVoucher}
                disabled={
                  isReadOnly || !voucherCode.trim() || isApplyingVoucher
                }
                handleButtonClick={handleApplyProviderVoucher}
              />
              {(appliedVoucherCode || voucherCode.trim()) && !isReadOnly && (
                <button
                  type="button"
                  className="text-sm text-[#4285F4] underline whitespace-nowrap"
                  onClick={handleClearProviderVoucher}
                  disabled={isApplyingVoucher}
                >
                  {t("clear_voucher_code")}
                </button>
              )}
            </div>
          </div>

          {fieldErrors.voucherCode && (
            <p className="mt-2 text-xs text-red-500" role="alert">
              {fieldErrors.voucherCode}
            </p>
          )}

          {!isReadOnly && (
            <AssignedVoucherPicker
              vouchers={usableAssignedVouchers}
              selectedCode={appliedVoucherCode || voucherCode}
              disabled={isApplyingVoucher || isReadOnly}
              variant="light"
              onSelect={(code) => {
                setVoucherCode(code);
                handleApplyProviderVoucher(code);
              }}
            />
          )}

          {voucherPreview && appliedVoucherCode && (
            <div className="mt-3 rounded-lg border border-[#d8e3fb] bg-white p-3 text-sm text-[#111c2d]">
              <div className="flex justify-between gap-2">
                <span>{t("platformFeeBeforeDiscount")}</span>
                <span>
                  $
                  {Number(
                    voucherPreview.workerShareBeforeDiscount.amount
                  ).toFixed(2)}{" "}
                  ({voucherPreview.workerShareBeforeDiscount.percentage}%)
                </span>
              </div>
              <div className="flex justify-between gap-2 text-green-700">
                <span>{t("voucher_discount")}</span>
                <span>
                  -$
                  {Number(voucherPreview.workerDiscount.amount).toFixed(2)}{" "}
                  ({voucherPreview.workerDiscount.percentage}%)
                </span>
              </div>
              <div className="flex justify-between gap-2">
                <span>{t("platformFeeAfterDiscount")}</span>
                <span>
                  $
                  {Number(
                    voucherPreview.workerShareAfterDiscount.amount
                  ).toFixed(2)}{" "}
                  ({voucherPreview.workerShareAfterDiscount.percentage}%)
                </span>
              </div>
              <div className="flex justify-between gap-2 font-semibold mt-1 pt-1 border-t border-[#e7eeff]">
                <span>{t("yourGuaranteedEarnings")}</span>
                <span>
                  $
                  {Number(
                    voucherPreview.providerEarningsAfterDiscount
                  ).toFixed(2)}
                </span>
              </div>
            </div>
          )}
        </div>

        <div className="pt-2 flex items-center justify-end border-t border-[#e7eeff]">
          <button
            type="submit"
            disabled={isSubmitting || isLoading || isReadOnly}
            className="h-10 px-6 rounded-lg bg-[#4285F4] text-white text-sm font-semibold shadow-sm hover:bg-[#4285F4] transition-colors disabled:opacity-60"
          >
            {isSubmitting
              ? t("submitting") || "Submitting..."
              : proposalSubmitLabel}
          </button>
        </div>
      </form>
        </>
      )}
    </div>
  );
};

export default NewProposalForm;
